<template>
  <v-app v-if="loggedIn">

    <SplashScreenLoader :loading="appLoading" />

    <AppBar :alerts="alerts.history" @update:toggleNavDrawer="navDrawer.state = !navDrawer.state" />

    <NavSidebar v-model="navDrawer.state" :nav="appNavList" :full="true" :persistent="false" breakpoint="md" />

    <TutorialTooltip v-if="!appLoading" @update:shown="navDrawer.state = true" />

    <v-main class="ma-3 d-flex flex-column align-center justify-start" style="min-height: 300px">

      <ToastAlert v-if="Object.keys(alerts.currentAlert).length" :type="alerts.currentAlert.type"
        :title="alerts.currentAlert.title" :text="alerts.currentAlert.text" :code="alerts.currentAlert.code" />

      <router-view />
    </v-main>
  </v-app>
</template>

<script>
import constants from '@/config/constants';

//Auth0
import { useAuth0 } from '@auth0/auth0-vue';

import { useUiStateStore } from '@/stores/uiState';

import orgService from '@/services/Org.service';

import SplashScreenLoader from '@/components/loaders/SplashScreenLoader';
import AppBar from '@/components/AppBar';
import NavSidebar from '@/components/NavSidebar';
import ToastAlert from '@/components/feedback/ToastAlert';

import TutorialTooltip from '@/components/tooltips/TutorialTooltip';

export default {
  name: 'App',
  data() {
    return {
      loggedIn: useAuth0().isAuthenticated,
      appLoading: true,
      alerts: {
        currentAlert: {},
        history: [],
      },
      navDrawer: {
        state: null,
      },
    }
  },
  components: {
    SplashScreenLoader,
    AppBar,
    NavSidebar,
    ToastAlert,
    TutorialTooltip,
  },
  computed: {
    appNavList() {
      const filterShowInNavFalse = (obj) => {
        const result = {};
        for (const key in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const item = obj[key];

            // Check if the item is an object and has the showInNav key
            if (typeof item === 'object' && item !== null && 'showInNav' in item) {
              if (item.showInNav !== false) {
                result[key] = { ...item };

                // If the item has children, filter them recursively
                if (item.children) {
                  const filteredChildren = filterShowInNavFalse(item.children);
                  if (Object.keys(filteredChildren).length > 0) {
                    result[key].children = filteredChildren;
                  } else {
                    delete result[key].children;
                  }
                }
              }
            } else {
              result[key] = item;
            }
          }
        }
        return result;
      };

      return filterShowInNavFalse(constants.nav);
    }
  },
  methods: {
    newAlert(type = 'info', title = 'Unknown', text, code) {
      this.alerts.currentAlert = {};

      let alert = {
        id: Date.now(),
        timestamp: Date.now(),
        type: type,
        title: title,
        text: text,
        code: code,
      };

      this.$nextTick(() => {

        this.alerts.currentAlert = alert;

        this.alerts.history.push(alert);

      });
    },
  },
  watch: {
    async loggedIn() {

      if (this.loggedIn) {

        const accessToken = await this.$auth0.getAccessTokenSilently();

        const { data, error } = await orgService.getBranding(accessToken);

        if (data) {
          //Set light theme colours
          this.$vuetify.theme.themes.light.colors.primary = data.primaryColor;
          this.$vuetify.theme.themes.light.colors.secondary = data.secondaryColor;

          //Set dark theme colours
          this.$vuetify.theme.themes.dark.colors.primary = data.primaryColor;
          this.$vuetify.theme.themes.dark.colors.secondary = data.secondaryColor;

          //Save theme to UI State store
          const UiStateStore = useUiStateStore();

          //Save light theme colours
          UiStateStore.branding.themes.light.primary = data.primaryColor;
          UiStateStore.branding.themes.light.secondary = data.secondaryColor;

          //Save dark theme colours
          UiStateStore.branding.themes.dark.primary = data.primaryColor;
          UiStateStore.branding.themes.dark.secondary = data.secondaryColor;

          this.appLoading = false;
        }

        if (error) {
          console.error(error);

          this.appLoading = false;
        }
      }
    },
  },
  async beforeMount() {

    //If a cookie is present for the theme
    if (this.$cookies.get('theme')) {

      //Read theme from cookie and set it in the global theme
      this.$vuetify.theme.global.name = this.$cookies.get('theme');

    } else {

      //If there is no cookie set, read the theme from the prefers-color-scheme query
      this.$vuetify.theme.global.name = window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }

  },
  provide() {
    return {
      newAlert: this.newAlert,
    };
  },
}
</script>

<style>
.sf-tutorial-highlight {
  border: #db0000 solid 2px;
  transition: border-color 1s;
}

.checkered-background {
  background-image:
    linear-gradient(45deg, rgb(var(--v-theme-background)) 25%, transparent 25%),
    linear-gradient(-45deg, rgb(var(--v-theme-background)) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, rgb(var(--v-theme-background)) 75%),
    linear-gradient(-45deg, transparent 75%, rgb(var(--v-theme-background)) 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  background-repeat: repeat;
  background-clip: padding-box;
}
</style>
